import * as React from 'react';
import { useState, useEffect } from 'react';

import {
    OutlinedInput,
    InputLabel,
    MenuItem,
    Select,
    FormControl,
    Stack,
    Chip,
    ListItemText,
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import FormHelperText from '@mui/material/FormHelperText';
import { Select as MuiSelect } from '@mui/material';
import { TextField as MuiTextField } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import FilterListIcon from '@mui/icons-material/FilterList';

import { listLabel } from '../../../utils/lists';

const generateRandomHex = (length) => {
    const randomNumber = Math.random().toString(16);
    return randomNumber.substr(2, length);
};

export default function MultiSelectChipsFilter({
    name,
    label,
    list,
    value,
    onChange,
    required,
    enableSelectAll,
    enableSelectNone,
    isError = false,
    errorMessage = null,
    fullWidth,
    formControlProps = {},
    labelProps = {},
    menuItemProps = {},
    selectProps = {},
    formHelperProps = {},
    enableTextFilter = false,
    enableSelectFilter = false,
    selectFilterList = [],
    defaultTextFilter = '',
    defaultSelectFilter = '',
}) {
    const [selectAll, setSelectAll] = useState(false);
    const [noOptionsSelected, setNoOptionsSelected] = useState(false);
    const [val, setVal] = useState(value);

    useEffect(() => {
        setVal(value);
    }, [value]);

    fullWidth = fullWidth === undefined ? true : fullWidth;

    const labelId = name + '-label';

    const id = React.useRef(generateRandomHex(8)).current;
    const selectFilterLabelId = id + '-select-filter-label';

    const [filterValue, setFilterValue] = React.useState(defaultTextFilter);
    const [selectFilterValue, setSelectFilterValue] = React.useState(defaultSelectFilter);
    const [filtersOpen, setFiltersOpen] = React.useState(false);

    const changeFilter = (e) => {
        setFilterValue(e.target.value);
    };

    const changeSelectFilter = (e) => {
        setSelectFilterValue(e.target.value);
    };

    const resetFilters = () => {
        setFilterValue('');
        setSelectFilterValue('');
    };

    const hasFilters = enableTextFilter || enableSelectFilter;

    const filteredList = React.useMemo(() => {
        let result = list;
        if (enableTextFilter && filterValue.trim()) {
            result = result.filter((cur) =>
                String(cur.label || '').toLowerCase().includes(filterValue.toLowerCase())
            );
        }
        if (enableSelectFilter && selectFilterValue) {
            result = result.filter((cur) =>
                String(cur.label || '').toLowerCase().includes(String(selectFilterValue).toLowerCase())
            );
        }
        return result;
    }, [list, enableTextFilter, filterValue, enableSelectFilter, selectFilterValue]);

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
                <FormControl fullWidth={fullWidth} error={isError} {...formControlProps}>
                    <InputLabel id={labelId} required={required} {...labelProps}>
                        {label}
                    </InputLabel>
                    <Select
                        multiple
                        labelId={labelId}
                        value={val}
                        onChange={(e) => {
                            setNoOptionsSelected(false);
                            if (e.target.value.includes('All')) {
                                if (!selectAll) {
                                    const allValues = filteredList.map((cur) => cur.value);
                                    setVal(allValues);
                                    onChange(allValues, e);
                                } else {
                                    setVal([]);
                                    onChange([], e);
                                }
                                setSelectAll(!selectAll);
                            } else if (e.target.value.includes('None')) {
                                setNoOptionsSelected(true);
                                setVal(['None']);
                                onChange(['None']);
                            } else {
                                if (enableSelectAll && e.target.value.length === filteredList.length) {
                                    setSelectAll(true);
                                }
                                setVal(e.target.value);
                                onChange(e.target.value, e);
                            }
                        }}
                        input={<OutlinedInput label={label} />}
                        renderValue={(selected) => (
                            <Stack gap={1} direction="row" flexWrap="wrap">
                                {selected.map((cur) => (
                                    <Chip
                                        key={cur}
                                        label={listLabel(cur, list)}
                                        onDelete={() => {
                                            setNoOptionsSelected(false);
                                            const newList = val.filter((item) => item !== cur);
                                            setVal(newList);
                                            onChange(newList);
                                        }}
                                        deleteIcon={
                                            <CancelIcon
                                                onMouseDown={(event) => event.stopPropagation()}
                                            />
                                        }
                                    />
                                ))}
                            </Stack>
                        )}
                        {...selectProps}
                    >
                        {enableSelectAll && (
                            <MenuItem key="All" value="All" disabled={noOptionsSelected} {...menuItemProps}>
                                <ListItemText
                                    primary={val.length === filteredList.length ? 'Unselect All' : 'Select All'}
                                />
                            </MenuItem>
                        )}
                        {enableSelectNone && (
                            <MenuItem key="None" value="None" {...menuItemProps}>
                                <ListItemText primary="No Animals Nearby" />
                            </MenuItem>
                        )}
                        {filteredList.map((cur) => (
                            <MenuItem
                                key={cur.value}
                                disabled={noOptionsSelected}
                                value={cur.value}
                                sx={{ justifyContent: 'space-between' }}
                                {...menuItemProps}
                            >
                                {cur.label}
                                {val.includes(cur.value) ? <CheckIcon color="info" /> : null}
                            </MenuItem>
                        ))}
                    </Select>
                    {errorMessage && (
                        <FormHelperText {...formHelperProps}>{errorMessage}</FormHelperText>
                    )}
                </FormControl>
                {hasFilters && (
                    <IconButton onClick={() => setFiltersOpen((prev) => !prev)} aria-label="Toggle filters">
                        <FilterListIcon />
                    </IconButton>
                )}
            </Stack>
            {hasFilters && (
                <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        {enableTextFilter && (
                            <MuiTextField
                                label={label + ' Free Filter'}
                                value={filterValue}
                                onChange={changeFilter}
                                fullWidth
                                size="small"
                            />
                        )}
                        {enableSelectFilter && (
                            <FormControl fullWidth size="small">
                                <InputLabel id={selectFilterLabelId}>{label + ' Select Filter'}</InputLabel>
                                <MuiSelect
                                    labelId={selectFilterLabelId}
                                    label={label + ' Select Filter'}
                                    value={selectFilterValue}
                                    onChange={changeSelectFilter}
                                >
                                    <MenuItem value=""><em>All</em></MenuItem>
                                    {selectFilterList.map((cur) => (
                                        <MenuItem key={cur.value || cur.label} value={cur.value}>{cur.label}</MenuItem>
                                    ))}
                                </MuiSelect>
                            </FormControl>
                        )}
                        <Button variant="outlined" size="small" onClick={resetFilters} sx={{ whiteSpace: 'nowrap' }}>Reset</Button>
                    </Stack>
                </Collapse>
            )}
        </Box>
    );
}
